/*
 * shipping-quote.js — métodos y precios de envío en la tienda.
 *
 * Le pregunta al servicio de tarifas (el mismo que llama Shopify desde el
 * checkout) qué métodos le llegan al pin del cliente, con las líneas del
 * carrito y su tipo de entrega. El servicio decide con la misma función que usa
 * en el checkout, así que la tienda y el checkout ofrecen lo mismo.
 *
 * - URL: ajuste del tema "Cotizador de envíos: URL del servicio"
 *   (shipping_quote_api_url), que llega en #cv-coverage-config como quoteUrl.
 *   Vacía: no se cotiza y cada lugar muestra solo la cobertura del mapa.
 * - El pin es exactamente el que se estampará en el carrito (CVPinStamp).
 *   Solo la barra y las etiquetas de envío gratis (opts.approximate) aceptan,
 *   sin pin, el centro del municipio elegido en el header; el resultado lleva
 *   approximate: true. Los métodos nunca se cotizan con ese punto.
 * - Las líneas salen de /cart?view=cv-envio (CVPinStamp.cartEnvio).
 * - Tipo de cliente: tipoCliente 'mayorista' solo para mayoristas, de #cv-coverage-config
 *   (customerType), como el _cv_cliente del carrito; sin él el servicio cotiza como minorista.
 * - Modo de pruebas del servicio: sin la marca de tarifas de prueba
 *   (debugRates) no hay opciones. Al recibir esa respuesta se deja de preguntar
 *   10 minutos, para no cargar el servicio desde el tema publicado.
 *
 * Nunca rechaza ni bloquea nada: ante cualquier falla, quien llama muestra la
 * etiqueta de cobertura de siempre.
 *
 * API (window.CelovendoShipping):
 *   enabled()                         hay URL configurada
 *   quoteCart(opts)                   cotiza el carrito; opts.referenceIfEmpty, opts.approximate
 *   quoteProduct(line)                cotiza un producto solo (1 unidad, sin el carrito)
 *   quoteReference(opts)              precios de referencia para el pin, sin el carrito; opts.approximate
 *   quote(request)                    cotización directa
 *   freeProgress(result)              cuánto falta para envío gratis (el método más cercano)
 *   freeThresholdFor(result, tipo)    umbral de envío gratis más bajo para un producto
 *   show(el, promise, opts)           pinta el resultado si sigue siendo el último
 *   render(el, result, opts)          pinta un resultado; false = usar respaldo
 *   fallbackLabel(coverage)           "Envío con mensajero (Guatemala)", etc.
 *   deliveryLabel(result)             { kind, text }: "Envío con PedidosYa", "Envío con Uber", "Envío con Cargo Expreso"
 *   icon(kind)                        SVG de moto (mensajero), camión (cargo_expreso) o tienda (recoger_tienda)
 *   choiceEnabled()                   ajuste "Elegir el método de envío en el carrito" (y hay URL)
 *   choosable(result)                 la cotización sirve para elegir (pin preciso, con opciones)
 *   defaultChoice(options, tipo)      mayorista en el polígono del tema: mensajero; si no, Cargo Expreso; nunca recoger
 *   selectedOption(result, tipo)      la que eligió el cliente si sigue en la lista, si no la predeterminada (o null)
 *   forgetChoice()                    olvida la elección (al cerrar el carrito y al pasar a la caja)
 *   choiceValue(option)               "<kind>:<code>", el valor de _cv_envio
 *   autoChoice(result)                Comprar ahora sin ventana: un solo envío, o todos gratis (el más rápido)
 *   choose(result, opts)              ventana para elegir (Comprar ahora); resuelve la opción o null
 *   invalidate()                      olvida las cotizaciones guardadas
 *   debounce(fn, ms)
 */
(function() {
  'use strict';

  var QUOTE_PATH = '/api/storefront/quote';
  var TIMEOUT_MS = 4000;
  var CACHE_TTL_MS = 60000;
  var PAUSE_MS = 10 * 60000;
  var STORAGE_PREFIX = 'cv_ship_q:';
  var PAUSE_KEY = 'cv_ship_pause_until';

  var configCache = null;
  var memory = {};
  var inflight = {};
  var showSeq = 0;

  function readConfig() {
    if (configCache) return configCache;
    var result = { url: '', debug: false, customerType: 'minorista', choice: true };
    try {
      var node = document.getElementById('cv-coverage-config');
      if (node) {
        var data = JSON.parse(node.textContent || node.innerText || '{}');
        result.url = String(data.quoteUrl || '').trim().replace(/\/+$/, '');
        result.debug = data.debugRates === true;
        if (data.customerType === 'mayorista') result.customerType = 'mayorista';
        if (data.shippingChoice === false) result.choice = false;
      }
    } catch (e) {}
    configCache = result;
    return result;
  }

  function enabled() {
    return !!readConfig().url;
  }

  /* --------------------------- almacenamiento --------------------------- */

  function storage() {
    try {
      return window.sessionStorage || null;
    } catch (e) {
      return null;
    }
  }

  function storageGet(key) {
    try {
      var s = storage();
      var raw = s && s.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      var s = storage();
      if (s) s.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  function paused() {
    var until = storageGet(PAUSE_KEY);
    return typeof until === 'number' && until > Date.now();
  }

  function invalidate() {
    memory = {};
    try {
      var s = storage();
      if (!s) return;
      var doomed = [];
      for (var i = 0; i < s.length; i++) {
        var key = s.key(i);
        if (key && key.indexOf(STORAGE_PREFIX) === 0) doomed.push(key);
      }
      doomed.forEach(function(key) { s.removeItem(key); });
    } catch (e) {}
  }

  function cached(key) {
    var entry = memory[key] || storageGet(STORAGE_PREFIX + key);
    if (!entry || typeof entry.at !== 'number' || Date.now() - entry.at > CACHE_TTL_MS) return null;
    memory[key] = entry;
    return entry.result;
  }

  function remember(key, result) {
    var entry = { at: Date.now(), result: result };
    memory[key] = entry;
    storageSet(STORAGE_PREFIX + key, entry);
  }

  /* ------------------------------ petición ------------------------------ */

  function normalizeLine(line) {
    return {
      tipo: String((line && line.tipo) || 'immediate'),
      bodega: String((line && line.bodega) || 'gt'),
      dias: Math.max(0, parseInt(line && line.dias, 10) || 0),
      quantity: Math.max(1, parseInt(line && line.quantity, 10) || 1),
      priceCents: Math.max(0, parseInt(line && line.priceCents, 10) || 0),
      requiresShipping: !(line && line.requiresShipping === false)
    };
  }

  function buildRequest(request) {
    var body = {
      lat: Number(request.lat),
      lng: Number(request.lng),
      debug: readConfig().debug
    };
    if (readConfig().customerType === 'mayorista') body.tipoCliente = 'mayorista';
    if (request.province) body.province = String(request.province);
    if (typeof request.subtotalCents === 'number' && isFinite(request.subtotalCents)) {
      body.subtotalCents = Math.max(0, Math.round(request.subtotalCents));
    }
    if (Array.isArray(request.lines) && request.lines.length) {
      body.lines = request.lines.map(normalizeLine);
    }
    return body;
  }

  function withTimeout(promise, controller) {
    return new Promise(function(resolve, reject) {
      var timer = setTimeout(function() {
        if (controller) {
          try { controller.abort(); } catch (e) {}
        }
        reject(new Error('tiempo'));
      }, TIMEOUT_MS);
      promise.then(function(value) {
        clearTimeout(timer);
        resolve(value);
      }, function(error) {
        clearTimeout(timer);
        reject(error);
      });
    });
  }

  function interpret(data) {
    var status = data && data.status;
    var options = data && Array.isArray(data.options) ? data.options : [];
    if (status === 'ok' && options.length) {
      return { ok: true, status: status, options: options, reference: data.reference === true, store: data.store || null, currency: data.currency || 'GTQ' };
    }
    if (status === 'modo-pruebas') return { ok: false, reason: 'modo-pruebas' };
    if (status === 'sin-opciones' || status === 'ok') return { ok: false, reason: 'sin-opciones' };
    return { ok: false, reason: 'error' };
  }

  /*
   * Cotización directa. request: { lat, lng, province?, subtotalCents?, lines? }.
   * Sin subtotalCents el servicio responde precios de referencia.
   */
  function quote(request) {
    var config = readConfig();
    if (!config.url) return Promise.resolve({ ok: false, reason: 'sin-url' });
    if (!request || !isFinite(Number(request.lat)) || !isFinite(Number(request.lng))) {
      return Promise.resolve({ ok: false, reason: 'sin-pin' });
    }
    if (!config.debug && paused()) return Promise.resolve({ ok: false, reason: 'modo-pruebas' });
    var body = buildRequest(request);
    var key = JSON.stringify([config.url, body]);
    var hit = cached(key);
    if (hit) return Promise.resolve(hit);
    if (inflight[key]) return inflight[key];
    if (typeof fetch !== 'function') return Promise.resolve({ ok: false, reason: 'error' });

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(body)
    };
    if (controller) init.signal = controller.signal;

    var promise = withTimeout(fetch(config.url + QUOTE_PATH, init), controller).then(function(response) {
      if (!response || !response.ok) return { ok: false, reason: 'error' };
      return response.json().then(interpret);
    }).catch(function(error) {
      return { ok: false, reason: error && error.message === 'tiempo' ? 'tiempo' : 'error' };
    }).then(function(result) {
      delete inflight[key];
      if (result.reason === 'modo-pruebas' && !config.debug) storageSet(PAUSE_KEY, Date.now() + PAUSE_MS);
      if (result.ok || result.reason === 'sin-opciones') remember(key, result);
      return result;
    });
    inflight[key] = promise;
    return promise;
  }

  /* --------------------------- carrito y producto --------------------------- */

  function pin() {
    try {
      var stamper = window.CVPinStamp;
      var stamp = stamper && typeof stamper.getStamp === 'function' ? stamper.getStamp() : null;
      if (!stamp) return null;
      var lat = parseFloat(stamp._cv_lat);
      var lng = parseFloat(stamp._cv_lng);
      if (!isFinite(lat) || !isFinite(lng)) return null;
      return { lat: lat, lng: lng };
    } catch (e) {
      return null;
    }
  }

  /*
   * Centro del municipio cuando el cliente eligió solo departamento y municipio
   * en el header (location-service.js: confirmed y precise === false). Es una
   * estimación: no se estampa y el checkout no la usa. Una ubicación precisa
   * sin pin (el cliente está moviendo el pin del mapa) no cuenta.
   */
  function approxPoint() {
    try {
      var location = window.CelovendoLocation && window.CelovendoLocation.getLocation && window.CelovendoLocation.getLocation();
      if (!location || location.confirmed !== true || location.precise !== false) return null;
      var lat = Number(location.lat);
      var lng = Number(location.lng);
      if (typeof location.lat !== 'number' || typeof location.lng !== 'number' || !isFinite(lat) || !isFinite(lng)) return null;
      return { lat: lat, lng: lng, approximate: true };
    } catch (e) {
      return null;
    }
  }

  function destination(opts) {
    var point = pin();
    if (point) return point;
    return opts && opts.approximate ? approxPoint() : null;
  }

  function province() {
    try {
      var location = window.CelovendoLocation && window.CelovendoLocation.getLocation && window.CelovendoLocation.getLocation();
      return (location && location.dept) || '';
    } catch (e) {
      return '';
    }
  }

  function cartEnvio() {
    var stamper = window.CVPinStamp;
    if (!stamper || typeof stamper.cartEnvio !== 'function') return Promise.resolve(null);
    return Promise.resolve(stamper.cartEnvio()).catch(function() { return null; });
  }

  function cartLines(data) {
    return data && Array.isArray(data.lines) ? data.lines.slice() : [];
  }

  /*
   * Cotiza el carrito actual. Con el carrito vacío y opts.referenceIfEmpty
   * pide precios de referencia (el panel de ubicación del header los muestra
   * como "desde"). Con opts.approximate y sin pin, cotiza con el centro del
   * municipio (solo para envío gratis).
   */
  function quoteCart(opts) {
    opts = opts || {};
    if (!enabled()) return Promise.resolve({ ok: false, reason: 'sin-url' });
    var point = destination(opts);
    if (!point) return Promise.resolve({ ok: false, reason: 'sin-pin' });
    return cartEnvio().then(function(data) {
      var lines = cartLines(data);
      if (!lines.length) {
        if (!opts.referenceIfEmpty) return { ok: false, reason: 'carrito-vacio' };
        return withSubtotal(quote({ lat: point.lat, lng: point.lng, province: province() }), null, point.approximate);
      }
      var subtotal = Number(data.subtotalCents) || 0;
      return withSubtotal(quote({ lat: point.lat, lng: point.lng, province: province(), subtotalCents: subtotal, lines: lines }), subtotal, point.approximate);
    });
  }

  // Copia del resultado con el subtotal cotizado (null: sin subtotal) y la marca
  // de punto aproximado, sin tocar el objeto que queda en caché.
  function withSubtotal(promise, subtotal, approximate) {
    return promise.then(function(result) {
      if (!result || !result.ok) return result;
      if (subtotal === null && !approximate) return result;
      var copy = {};
      for (var key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) copy[key] = result[key];
      }
      if (subtotal !== null) copy.subtotalCents = subtotal;
      if (approximate) copy.approximate = true;
      return copy;
    });
  }

  // Precios de referencia para el pin del cliente, sin el carrito (etiquetas "Envío gratis").
  function quoteReference(opts) {
    if (!enabled()) return Promise.resolve({ ok: false, reason: 'sin-url' });
    var point = destination(opts);
    if (!point) return Promise.resolve({ ok: false, reason: 'sin-pin' });
    return withSubtotal(quote({ lat: point.lat, lng: point.lng, province: province() }), null, point.approximate);
  }

  /*
   * Métodos, precios y tiempos de un producto solo: 1 unidad a su precio, sin
   * el carrito. Así la ficha muestra siempre lo mismo para ese producto; con el
   * carrito cambiaban los precios (envío gratis por lo demás que llevaba) y los
   * tiempos ("Algunos productos listos en 10 min y otros en 7 días hábiles").
   * El carrito y el checkout cotizan el pedido completo.
   * line: { variantId, tipo, bodega, dias, priceCents }.
   */
  function quoteProduct(line) {
    if (!enabled()) return Promise.resolve({ ok: false, reason: 'sin-url' });
    if (!line || line.tipo === 'agotado') return Promise.resolve({ ok: false, reason: 'agotado' });
    var point = pin();
    if (!point) return Promise.resolve({ ok: false, reason: 'sin-pin' });
    var product = normalizeLine({ tipo: line.tipo, bodega: line.bodega, dias: line.dias, quantity: 1, priceCents: line.priceCents });
    return withSubtotal(quote({ lat: point.lat, lng: point.lng, province: province(), subtotalCents: product.priceCents, lines: [product] }), product.priceCents);
  }

  /* ----------------------------- envío gratis ----------------------------- */

  var PICKUP_KIND = 'recoger_tienda';
  // Tipos de entrega que el servicio manda solo por Cargo Expreso.
  var CARGO_ONLY = { backorder: true, slowback: true, amazon: true, marketplace: true };

  function isFreeOption(option) {
    return !!option && (option.free === true || Number(option.priceCents) === 0);
  }

  function hasThreshold(option) {
    return !!option && option.freeThresholdCents !== null && option.freeThresholdCents !== undefined &&
      isFinite(Number(option.freeThresholdCents));
  }

  /*
   * Cuánto le falta al carrito para envío gratis, con los mismos umbrales que
   * el checkout (por cobertura, tipo de cliente y método). Recoger en tienda no
   * cuenta. Devuelve:
   *   { needsLocation: true }       todavía no hay pin preciso
   *   { reached: true, name, kind, thresholdCents } algún método ya sale gratis
   *                                 (thresholdCents null si el servicio no da el monto)
   *   { reached: false, remainingCents, thresholdCents, pct, name, kind, reference } el más cercano
   *   null                          sin cotización (servicio caído, modo de pruebas,
   *                                 sin opciones) o ningún método puede salir gratis
   * Con una cotización aproximada (centro del municipio) reached y remaining
   * llevan approximate: true.
   * Nunca inventa un monto: sin cotización no hay barra.
   * El faltante se calcula como el texto del servicio: umbral - subtotal cotizado.
   * Con una cotización de referencia (carrito vacío) el subtotal es 0.
   */
  function freeProgress(result) {
    if (result && result.reason === 'sin-pin') return { needsLocation: true };
    if (!result || !result.ok || !result.options || !result.options.length) return null;
    var shipping = result.options.filter(function(option) { return option && option.kind !== PICKUP_KIND; });
    var free = null;
    shipping.forEach(function(option) {
      if (!free && isFreeOption(option)) free = option;
    });
    if (free && !result.reference) {
      return markApproximate({ reached: true, name: free.name || '', kind: kindOf(free), thresholdCents: hasThreshold(free) ? Number(free.freeThresholdCents) : null }, result);
    }
    var subtotal = result.reference ? 0 : Math.max(0, Number(result.subtotalCents) || 0);
    var best = null;
    shipping.forEach(function(option) {
      if (!hasThreshold(option)) return;
      var threshold = Number(option.freeThresholdCents);
      var remaining = Math.max(0, threshold - subtotal);
      if (!best || remaining < best.remainingCents) {
        best = { option: option, remainingCents: remaining, thresholdCents: threshold };
      }
    });
    if (!best) return null;
    if (best.remainingCents === 0) return markApproximate({ reached: true, name: best.option.name || '', kind: kindOf(best.option), thresholdCents: best.thresholdCents }, result);
    return markApproximate({
      reached: false,
      remainingCents: best.remainingCents,
      thresholdCents: best.thresholdCents,
      pct: best.thresholdCents > 0 ? Math.min(100, Math.floor(subtotal * 100 / best.thresholdCents)) : 0,
      name: best.option.name || '',
      kind: kindOf(best.option),
      reference: result.reference === true
    }, result);
  }

  function markApproximate(progress, result) {
    if (result.approximate === true) progress.approximate = true;
    return progress;
  }

  /*
   * Umbral de envío gratis más bajo que le aplica a un producto con este tipo
   * de entrega, o null si no puede salir gratis. Bajo pedido y marketplace
   * solo cuentan Cargo Expreso, como decide el servicio.
   */
  function freeThresholdFor(result, tipo) {
    if (!result || !result.ok || !result.options) return null;
    if (tipo === 'agotado') return null;
    var cargoOnly = CARGO_ONLY[String(tipo || '')] === true;
    var min = null;
    result.options.forEach(function(option) {
      if (!option || option.kind === PICKUP_KIND || !hasThreshold(option)) return;
      if (cargoOnly && kindOf(option) !== 'cargo_expreso') return;
      var threshold = Number(option.freeThresholdCents);
      if (min === null || threshold < min) min = threshold;
    });
    return min;
  }

  /* ------------------------------- pintar ------------------------------- */

  function escapeHTML(value) {
    return String(value === undefined || value === null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function money(cents) {
    var value = (Math.max(0, Number(cents) || 0) / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'Q' + parts.join('.');
  }

  function priceLabel(option, reference) {
    if (option.free || Number(option.priceCents) === 0) return 'Gratis';
    return (reference ? 'desde ' : '') + money(option.priceCents);
  }

  function hide(el) {
    el.innerHTML = '';
    el.hidden = true;
  }

  // Moto para el mensajero (PedidosYa, Uber), camión para Cargo Expreso y tienda para recoger.
  var ICON_OPEN = '<svg class="cv-ship-options__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">';
  var ICONS = {
    mensajero: ICON_OPEN + '<circle cx="5" cy="16" r="3"/><circle cx="19" cy="16" r="3"/><path d="M7.5 14h5l4-4H6m1.5 4l4-4"/><path d="M13 6h2l1.5 3 2 4"/></svg>',
    cargo_expreso: ICON_OPEN + '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
    recoger_tienda: ICON_OPEN + '<path d="M3 9l2-5h14l2 5"/><path d="M3 9h18a3 3 0 0 1-6 0a3 3 0 0 1-6 0a3 3 0 0 1-6 0"/><path d="M5 12.5V20h14v-7.5"/><path d="M10 20v-5h4v5"/></svg>'
  };

  function kindOf(option) {
    return option && option.kind === 'cargo_expreso' ? 'cargo_expreso' : 'mensajero';
  }

  function icon(kind) {
    return ICONS[kind === 'cargo_expreso' || kind === PICKUP_KIND ? kind : 'mensajero'];
  }

  // Ícono y clase de cada opción: recoger en tienda aparte del mensajero.
  function displayKind(option) {
    return option && option.kind === PICKUP_KIND ? PICKUP_KIND : kindOf(option);
  }

  // "Ver dirección" de recoger en tienda (cv-pickup-store.js), si hay datos de esa tienda.
  function storeButton(option) {
    var stores = window.CVPickupStore;
    if (!option.store || !stores || typeof stores.has !== 'function' || !stores.has(option.store)) return '';
    return '<button type="button" class="cv-ship-options__store" data-cv-pickup-store="' + escapeHTML(option.store) +
      '" data-cv-pickup-name="' + escapeHTML(option.name) + '">Ver dirección</button>';
  }

  // Más rápido primero: el mensajero llega antes que Cargo Expreso. Empates: el orden del servicio.
  function byTime(options) {
    return options.map(function(option, index) { return { option: option, index: index }; })
      .sort(function(a, b) {
        var rank = (kindOf(a.option) === 'cargo_expreso' ? 1 : 0) - (kindOf(b.option) === 'cargo_expreso' ? 1 : 0);
        return rank || a.index - b.index;
      })
      .map(function(entry) { return entry.option; });
  }

  // Recoger en tienda siempre al final; las demás quedan en el orden que traen.
  function pickupLast(options) {
    var shipping = options.filter(function(option) { return !option || option.kind !== PICKUP_KIND; });
    var pickup = options.filter(function(option) { return option && option.kind === PICKUP_KIND; });
    return shipping.concat(pickup);
  }

  /*
   * Pinta las opciones en `el`. Devuelve false (y oculta `el`) si no hay nada
   * que mostrar: entonces quien llama muestra su etiqueta de cobertura.
   * opts: { title, compact, eta, fastestFirst, icons, note }. eta: rango de
   * entrega de cada método (por defecto solo sin compact). fastestFirst: el
   * mensajero antes que Cargo Expreso (el servicio los ordena por precio).
   * Recoger en tienda va siempre al final.
   * icons: moto, camión o tienda. note: false quita "El costo se confirma al pagar".
   * selectable: radios para elegir el método (solo con una cotización que sirva
   * para elegir, ver choosable); la predeterminada va arriba (orderForChoice),
   * va marcada la de selectedOption (o opts.selected) y al cambiar se guarda y se
   * llama a opts.onChange(option). opts.name: name de los radios.
   */
  function render(el, result, opts) {
    if (!el) return false;
    opts = opts || {};
    if (!result || !result.ok || !result.options || !result.options.length) {
      hide(el);
      return false;
    }
    var html = '';
    var selectable = !!opts.selectable && choosable(result);
    var title = opts.title === undefined ? (selectable ? '¿Cómo quieres recibir tu pedido?' : 'Envío a tu ubicación') : opts.title;
    var showEta = opts.eta === undefined ? !opts.compact : !!opts.eta;
    if (title) html += '<p class="cv-ship-options__title">' + escapeHTML(title) + '</p>';
    var list = selectable ? orderForChoice(result.options, opts.customerType) : pickupLast(opts.fastestFirst ? byTime(result.options) : result.options);
    var selected = selectable ? (opts.selected !== undefined ? opts.selected : selectedOption(result, opts.customerType)) : null;
    var radioName = escapeHTML(opts.name || 'cv-envio');
    html += '<ul class="cv-ship-options__list' + (opts.icons ? ' cv-ship-options__list--icons' : '') + (selectable ? ' cv-ship-options__list--select' : '') + '"' +
      (selectable ? ' role="radiogroup" aria-label="' + escapeHTML(title || 'Método de envío') + '"' : '') + '>';
    var freeText = '';
    list.forEach(function(option) {
      var kind = displayKind(option);
      var price = priceLabel(option, result.reference);
      var body = '<span class="cv-ship-options__name">' + (opts.icons ? icon(kind) : '') + escapeHTML(option.name) + '</span>' +
        '<span class="cv-ship-options__price' + (price === 'Gratis' ? ' cv-ship-options__price--free' : '') + '">' + escapeHTML(price) + '</span>';
      if (option.estimatedDelivery && showEta) {
        body += '<span class="cv-ship-options__eta">' + escapeHTML(option.estimatedDelivery) + '</span>';
      }
      if (selectable) {
        var checked = option === selected;
        html += '<li class="cv-ship-options__item cv-ship-options__item--' + kind + ' cv-ship-options__item--select' + (checked ? ' is-selected' : '') + '">' +
          '<label class="cv-ship-options__choice"><input type="radio" class="cv-ship-options__radio" name="' + radioName +
          '" value="' + escapeHTML(choiceValue(option)) + '"' + (checked ? ' checked' : '') + '>' + body + '</label>';
      } else {
        html += '<li class="cv-ship-options__item cv-ship-options__item--' + kind + '">' + body;
      }
      if (option.kind === PICKUP_KIND) html += storeButton(option);
      html += '</li>';
      if (!freeText && !result.reference && option.textFreeThreshold && !(option.free || Number(option.priceCents) === 0)) {
        freeText = option.textFreeThreshold;
      }
    });
    html += '</ul>';
    if (freeText && !opts.compact) html += '<p class="cv-ship-options__free">' + escapeHTML(freeText) + '</p>';
    if (opts.note !== false) {
      html += '<p class="cv-ship-options__note">' + (result.reference ? 'Precio según tu compra. ' : '') + 'El costo se confirma al pagar.</p>';
    }
    el.innerHTML = html;
    el.hidden = false;
    if (selectable) bindChoice(el, list, opts.onChange, opts.remember, selected);
    return true;
  }

  /*
   * Pinta el resultado de `promise` solo si sigue siendo la última cotización
   * pedida para `el`. Resuelve true si mostró opciones.
   */
  function show(el, promise, opts) {
    if (!el) return Promise.resolve(false);
    var token = String(++showSeq);
    el.setAttribute('data-cv-ship-token', token);
    return Promise.resolve(promise).then(function(result) {
      if (el.getAttribute('data-cv-ship-token') !== token) return false;
      return render(el, result, opts);
    }, function() {
      if (el.getAttribute('data-cv-ship-token') !== token) return false;
      return render(el, null, opts);
    });
  }

  // "PedidosYa" o "Uber" según el nombre o el código que da el servicio
  // ("Envio con Mensajero - Uber Moto", UBER12); si no, lo que va después del guion.
  function messengerName(option) {
    var text = String((option && option.name) || '') + ' ' + String((option && option.code) || '');
    if (/pedidos\s*ya/i.test(text)) return 'PedidosYa';
    if (/uber/i.test(text)) return 'Uber';
    var name = String((option && option.name) || '').trim();
    var dash = name.lastIndexOf(' - ');
    return dash >= 0 ? name.slice(dash + 3).trim() : '';
  }

  /*
   * Cómo llega el envío según la cotización: { kind, text }. Con mensajero,
   * "Envío con PedidosYa" / "Envío con Uber" (los dos si el servicio ofrece
   * ambos); si no, "Envío con Cargo Expreso". Recoger en tienda no cuenta.
   * null sin cotización o sin métodos de envío.
   */
  function deliveryLabel(result) {
    if (!result || !result.ok || !result.options) return null;
    var names = [];
    var cargo = false;
    result.options.forEach(function(option) {
      if (!option || option.kind === PICKUP_KIND) return;
      if (kindOf(option) === 'cargo_expreso') {
        cargo = true;
        return;
      }
      var name = messengerName(option) || 'mensajero';
      if (names.indexOf(name) === -1) names.push(name);
    });
    if (names.length) return { kind: 'mensajero', text: 'Envío con ' + names.join(' o ') };
    if (cargo) return { kind: 'cargo_expreso', text: 'Envío con Cargo Expreso' };
    return null;
  }

  // Texto de cobertura cuando el servicio no tiene opciones que mostrar.
  function fallbackLabel(coverage) {
    var method = coverage && coverage.delivery_method;
    var zone = coverage && coverage.delivery_zone;
    if (method === 'mensajero') {
      if (zone === 'guatemala') return 'Envío con mensajero (Guatemala)';
      if (zone === 'xela') return 'Envío con mensajero (Xela)';
      return 'Envío con mensajero';
    }
    if (method === 'cargo_expreso') return 'Envío con Cargo Expreso';
    return '';
  }

  function debounce(fn, ms) {
    var timer = null;
    return function() {
      var self = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() { fn.apply(self, args); }, ms || 350);
    };
  }

  /* ----------------------------- método elegido ----------------------------- */

  /*
   * El cliente elige el método en el carrito (o en la ventana de Comprar ahora)
   * y cart-pin-stamp.js lo estampa en cada línea como _cv_envio = "<kind>:<code>".
   * El servicio de tarifas deja en la caja solo esa tarifa; si ya no está, todas.
   * La elección vive solo en memoria, mientras el carrito está abierto (el carrito
   * lateral rehace su HTML al cambiar una cantidad): no se guarda en el navegador,
   * así que al cerrar el carrito, recargar o cambiar de página vuelve la predeterminada.
   */
  var chosen = null;
  // Una elección que guardaron versiones anteriores del tema en sessionStorage.
  try { window.sessionStorage.removeItem('cv_envio_sel'); } catch (e) {}

  function choiceEnabled() {
    return enabled() && readConfig().choice;
  }

  // Solo con el pin que se va a estampar: nunca con el punto aproximado ni con precios de referencia.
  function choosable(result) {
    return !!(result && result.ok && !result.approximate && !result.reference && result.options && result.options.length);
  }

  function choiceValue(option) {
    return option ? displayKind(option) + ':' + String(option.code || '') : '';
  }

  function firstOfKind(options, kind) {
    for (var i = 0; i < options.length; i++) {
      if (options[i] && displayKind(options[i]) === kind) return options[i];
    }
    return null;
  }

  // ¿El pin cae en el polígono de mensajero del tema? Es la clasificación que
  // se estampa como _cv_metodo (delivery-coverage.js), no la del servicio.
  function insideThemeZone() {
    try {
      var stamper = window.CVPinStamp;
      var stamp = stamper && typeof stamper.getStamp === 'function' ? stamper.getStamp() : null;
      return !!(stamp && stamp._cv_metodo === 'mensajero');
    } catch (e) {
      return false;
    }
  }

  /*
   * Marcada de entrada: al mayorista con el pin dentro del polígono del tema, el
   * mensajero (gratis o no) si el servicio lo ofrece; a todos los demás, Cargo
   * Expreso. Recoger en tienda nunca: si es lo único que hay, null y el cliente
   * tiene que tocarla.
   */
  function defaultChoice(options, customerType) {
    if (!options || !options.length) return null;
    var type = customerType || readConfig().customerType;
    if (type === 'mayorista' && insideThemeZone()) {
      var messenger = firstOfKind(options, 'mensajero');
      if (messenger) return messenger;
    }
    var shipping = byTime(options).filter(function(option) { return option && option.kind !== PICKUP_KIND; });
    return firstOfKind(options, 'cargo_expreso') || shipping[0] || null;
  }

  // La predeterminada arriba; después el mensajero antes que Cargo Expreso y recoger en tienda al final.
  function orderForChoice(options, customerType) {
    var preferred = defaultChoice(options, customerType);
    var rest = pickupLast(byTime(options.filter(function(option) { return option !== preferred; })));
    return preferred ? [preferred].concat(rest) : rest;
  }

  function storedChoice() {
    return chosen;
  }

  function forgetChoice() {
    chosen = null;
  }

  function rememberChoice(option) {
    chosen = option ? { kind: displayKind(option), code: String(option.code || '') } : null;
  }

  /*
   * La opción marcada para esta cotización: la que eligió el cliente si sigue en
   * la lista (el mensajero aunque haya cambiado de cobertura y de código; una
   * tienda solo si es la misma), si no la predeterminada. null si no se puede elegir.
   */
  function selectedOption(result, customerType) {
    if (!choosable(result)) return null;
    var options = result.options;
    var stored = storedChoice();
    if (stored) {
      for (var i = 0; i < options.length; i++) {
        if (options[i] && displayKind(options[i]) === stored.kind && String(options[i].code || '') === stored.code) return options[i];
      }
      if (stored.kind !== PICKUP_KIND) {
        var sameKind = firstOfKind(options, stored.kind);
        if (sameKind) return sameKind;
      }
    }
    return defaultChoice(options, customerType);
  }

  function onChoiceChange(event) {
    var target = event && event.target;
    var state = this._cvShipChoice;
    if (!state || !target || target.type !== 'radio') return;
    var option = null;
    for (var i = 0; i < state.options.length; i++) {
      if (choiceValue(state.options[i]) === target.value) option = state.options[i];
    }
    if (!option) return;
    if (state.remember !== false) rememberChoice(option);
    state.selected = option;
    var items = this.querySelectorAll ? this.querySelectorAll('.cv-ship-options__item--select') : [];
    for (var j = 0; j < items.length; j++) {
      var radio = items[j].querySelector('.cv-ship-options__radio');
      items[j].classList.toggle('is-selected', !!(radio && radio.checked));
    }
    if (typeof state.onChange === 'function') state.onChange(option);
  }

  function bindChoice(el, options, onChange, remember, selected) {
    el._cvShipChoice = { options: options, onChange: onChange, remember: remember, selected: selected || null };
    if (el.getAttribute('data-cv-choice-bound') || typeof el.addEventListener !== 'function') return;
    el.setAttribute('data-cv-choice-bound', '1');
    el.addEventListener('change', onChoiceChange);
  }

  /*
   * Comprar ahora sin preguntar: si hay un solo método de envío (por ejemplo
   * Cargo Expreso y recoger en tienda), ese; si todos los métodos de envío son
   * gratis, el más rápido (el mensajero antes que Cargo Expreso). Recoger en
   * tienda no cuenta. null: hay que abrir la ventana.
   */
  function autoChoice(result) {
    if (!choosable(result)) return null;
    var shipping = byTime(result.options).filter(function(option) { return option && option.kind !== PICKUP_KIND; });
    if (shipping.length === 1) return shipping[0];
    if (!shipping.length) return null;
    var allFree = shipping.every(function(option) { return option.free === true || Number(option.priceCents) === 0; });
    return allFree ? shipping[0] : null;
  }

  /*
   * Ventana "¿Cómo quieres recibir tu pedido?" para Comprar ahora. Resuelve la
   * opción elegida al tocar "Continuar a pagar", o null si la cierran (X, Esc o
   * fuera de la ventana). No se abre si autoChoice ya decide; sin cotización que
   * sirva, null. No guarda la elección.
   */
  function choose(result, opts) {
    opts = opts || {};
    if (!choosable(result)) return Promise.resolve(null);
    var auto = autoChoice(result);
    if (auto) return Promise.resolve(auto);
    return new Promise(function(resolve) {
      var previous = document.activeElement;
      var overlay = document.createElement('div');
      overlay.className = 'cv-ship-modal';
      overlay.innerHTML = '<div class="cv-ship-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cv-ship-modal-title">' +
        '<button type="button" class="cv-ship-modal__close" aria-label="Cerrar">&times;</button>' +
        '<p class="cv-ship-modal__title" id="cv-ship-modal-title">¿Cómo quieres recibir tu pedido?</p>' +
        '<div class="cv-ship-options cv-ship-modal__options"></div>' +
        '<button type="button" class="cv-ship-modal__continue">Continuar a pagar</button>' +
        '</div>';
      document.body.appendChild(overlay);
      var current = defaultChoice(result.options, opts.customerType);
      var continueButton = overlay.querySelector('.cv-ship-modal__continue');
      continueButton.disabled = !current;
      render(overlay.querySelector('.cv-ship-modal__options'), result, {
        selectable: true, title: '', icons: true, eta: true, note: false, name: 'cv-envio-ventana', selected: current,
        customerType: opts.customerType, remember: false,
        onChange: function(option) { current = option; continueButton.disabled = false; }
      });
      function finish(value) {
        document.removeEventListener('keydown', onKey, true);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        try { if (previous && previous.focus) previous.focus(); } catch (e) {}
        resolve(value);
      }
      function onKey(event) {
        if (event.key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          finish(null);
        }
      }
      document.addEventListener('keydown', onKey, true);
      overlay.addEventListener('click', function(event) {
        var target = event.target;
        if (target === overlay || (target.closest && target.closest('.cv-ship-modal__close'))) {
          finish(null);
        } else if (current && target.closest && target.closest('.cv-ship-modal__continue')) {
          finish(current);
        }
      });
      var focusTarget = overlay.querySelector('.cv-ship-options__radio:checked') || overlay.querySelector('.cv-ship-modal__continue');
      if (focusTarget && focusTarget.focus) focusTarget.focus();
    });
  }

  window.CelovendoShipping = {
    enabled: enabled,
    quote: quote,
    quoteCart: quoteCart,
    quoteProduct: quoteProduct,
    quoteReference: quoteReference,
    freeProgress: freeProgress,
    freeThresholdFor: freeThresholdFor,
    money: money,
    render: render,
    show: show,
    fallbackLabel: fallbackLabel,
    deliveryLabel: deliveryLabel,
    icon: icon,
    choiceEnabled: choiceEnabled,
    choosable: choosable,
    defaultChoice: defaultChoice,
    orderForChoice: orderForChoice,
    selectedOption: selectedOption,
    rememberChoice: rememberChoice,
    forgetChoice: forgetChoice,
    choiceValue: choiceValue,
    autoChoice: autoChoice,
    choose: choose,
    invalidate: invalidate,
    debounce: debounce
  };
})();
